import type { JobType } from "@repo/app-types/entities/jobType";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { Size } from "@repo/ui/helpers/helpers";
import { IconPencil } from "@tabler/icons-react";
import { type FC, useRef, useState } from "react";
import { useUpdateJobTypeMutation } from "../../../../api/entities/jobType.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../../../hooks/hook.js";
import {
  NotificationType,
  showNotification,
} from "../../../notificationsOverlay/notificationsSlice.js";
import { fetchProjectById } from "../../projectsSlice.js";

interface JobTypeNameProps {
  jobType: JobType;
  projectId: string;
}

const MAX_JOB_TYPE_NAME_LENGTH = 150;

export const JobTypeName: FC<JobTypeNameProps> = ({ jobType, projectId }) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const [updateJobType, { isLoading: isSaving }] = useUpdateJobTypeMutation();

  const onStopEditing = () => {
    setIsEditing(false);
    setEditedName(undefined);
  };

  const onSave = async () => {
    // Just stop editing if the name is invalid
    if (
      !editedName ||
      editedName.length < 3 ||
      editedName.length > MAX_JOB_TYPE_NAME_LENGTH
    ) {
      onStopEditing();
      return;
    }

    try {
      // Wait for the mutation to complete
      await updateJobType({
        id: jobType.id,
        name: editedName,
      }).unwrap();

      // Now refresh the project after successful update
      await dispatch(fetchProjectById(projectId)).unwrap();

      onStopEditing();
    } catch (err) {
      const apiError = err as {
        data?: {
          error?: string;
        };
      };
      showNotification(dispatch, {
        id: `job-type-name-error-${Date.now().toString()}`,
        primaryMessage:
          apiError.data?.error ??
          "An error occurred while updating the job type name",
        type: NotificationType.FAILURE,
      });
      onStopEditing();
    }
  };

  if (!isEditing) {
    return (
      <div className="group flex items-center space-x-2">
        <Label size={Size.LARGE}>{jobType.name}</Label>
        <Can I={PermissionActions.UPDATE} a={PermissionSubjects.JOB_OPENINGS}>
          <div
            className="mb-[5px] h-fit cursor-pointer rounded-md p-1 hover:bg-slate-200"
            onClick={() => {
              setIsEditing(true);
            }}
          >
            <IconPencil className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
          </div>
        </Can>
      </div>
    );
  }

  return (
    <div>
      <Input
        autoFocus
        inputClassName="max-w-[400px]"
        ref={inputRef}
        maxLength={MAX_JOB_TYPE_NAME_LENGTH}
        onChange={(e) => {
          setEditedName(e.target.value);
        }}
        value={editedName ?? jobType.name}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void onSave();
          }
        }}
        onBlur={() => {
          void onSave();
        }}
        placeholder="Store Manager - San Francisco"
        isDisabled={isSaving}
      />
    </div>
  );
};
